import { Helmet } from "react-helmet-async";
import { defaultSEOConfig, type SEOConfig } from "./seo-config";
import { getBlogPageCriticalModulesStatic } from '../../lib/manifest-utils';
import { useLocation } from 'react-router-dom';

interface SEOProps extends Partial<SEOConfig> {
  children?: React.ReactNode;
  /**
   * The LCP image URL for the current page (used for dynamic preload)
   */
  lcpImage?: string;
  /**
   * Optional: specify the current route if SEO is used outside Router context
   */
  currentPath?: string;
  /**
   * Robots meta tag directive (e.g., 'index, follow' or 'noindex, follow')
   */
  robots?: string;
}

// Safe hook to get location without breaking outside Router context
function useSafeLocation() {
  try {
    return useLocation();
  } catch (error) {
    // If useLocation fails (outside Router context), return null
    return null;
  }
}

export function SEO({
  title,
  description,
  canonical,
  openGraph,
  twitter,
  children,
  lcpImage,
  currentPath,
  robots,
}: SEOProps) {
  const location = useSafeLocation();
  const pathname = currentPath || location?.pathname || '';
  
  // AUTO-GENERATE CANONICAL URL: Fix for duplicate content indexing issues
  // This ensures every page has a proper canonical URL, eliminating duplicate content problems
  const generateCanonicalUrl = (pathname: string): string => {
    const baseUrl = 'https://www.diyecohomes.com';
    
    // Handle special cases for clean canonical URLs
    if (pathname === '/' || pathname === '') {
      return baseUrl;
    }
    
    // For blog category pages, create clean canonical URLs
    if (pathname === '/blog') {
      // Get current URL search params to handle category filtering
      const currentUrl = typeof window !== 'undefined' ? window.location : null;
      if (currentUrl && currentUrl.search) {
        const params = new URLSearchParams(currentUrl.search);
        const category = params.get('category');
        const searchQuery = params.get('q');
        const page = params.get('page');
        
        // For category pages, create canonical with category parameter
        if (category && !searchQuery) {
          return `${baseUrl}/blog?category=${encodeURIComponent(category)}`;
        }
        
        // For search results, use noindex so no canonical needed, return base blog
        if (searchQuery) {
          return `${baseUrl}/blog`;
        }
        
        // For pagination, canonical should point to page 1 (without page param)
        if (page && !category && !searchQuery) {
          return `${baseUrl}/blog`;
        }
        
        // For paginated category pages, include category but not page
        if (page && category && !searchQuery) {
          return `${baseUrl}/blog?category=${encodeURIComponent(category)}`;
        }
      }
      
      // Default blog canonical
      return `${baseUrl}/blog`;
    }
    
    // For all other pages, use pathname as-is
    return `${baseUrl}${pathname}`;
  };
  
  // Use provided canonical or auto-generate one
  const canonicalUrl = canonical || generateCanonicalUrl(pathname);
  
  const seo = {
    title: title || defaultSEOConfig.title,
    titleTemplate: defaultSEOConfig.titleTemplate,
    description: description || defaultSEOConfig.description,
    canonical: canonicalUrl,
    openGraph: {
      ...defaultSEOConfig.openGraph,
      ...openGraph,
      title: openGraph?.title || title || defaultSEOConfig.openGraph.title,
      description: openGraph?.description || description || defaultSEOConfig.openGraph.description,
    },
    twitter: {
      ...defaultSEOConfig.twitter,
      ...twitter,
    },
  };

  const isHomePage = pathname === '/';
  const isBlogPage = pathname.startsWith('/blog');

  // Generate responsive preload links for LCP image
  function renderLcpPreloads(lcpImage: string) {
    if (!lcpImage) return null;
    
    // Extract the base name without extension for responsive variants
    const baseName = lcpImage.replace(/\.(jpg|jpeg|webp|png)$/i, '');
    const extension = lcpImage.match(/\.(jpg|jpeg|webp|png)$/i)?.[0] || '';
    
    return (
      <>
        {/* WebP responsive variants (these exist and are generated by build process) */}
        <link rel="preload" as="image" href={`${baseName}-mobile.webp`} imagesrcset={`${baseName}-mobile.webp 320w, ${baseName}-tablet.webp 768w, ${baseName}-desktop.webp 1024w, ${baseName}-xl.webp 1920w`} imagesizes="100vw" type="image/webp" />
        {/* Original JPEG as fallback (always exists) */}
        <link rel="preload" as="image" href={lcpImage} type={extension === '.png' ? 'image/png' : 'image/jpeg'} />
      </>
    );
  }

  return (
    <Helmet>
      {/* General tags */}
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <link rel="canonical" href={seo.canonical} />
      {robots && <meta name="robots" content={robots} />}

      {/* Favicon tags */}
      <link rel="icon" href="/favicon.ico" sizes="any" />
      <link rel="icon" type="image/svg+xml" href="/images/brand/logo.svg" />
      <link rel="apple-touch-icon" sizes="180x180" href="/images/brand/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/images/brand/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/images/brand/favicon-16x16.png" />
      <link rel="manifest" href="/site.webmanifest" />
      <meta name="theme-color" content="#18181b" />

      {/* Preload LCP hero image for home page only */}
      {isHomePage && (
        <>
          <link rel="preload" as="image" href="/images/eco-cabin-hero-mobile.webp" imagesrcset="/images/eco-cabin-hero-mobile.webp 320w, /images/eco-cabin-hero-tablet.webp 768w, /images/eco-cabin-hero-desktop.webp 1024w, /images/eco-cabin-hero-xl.webp 1920w" imagesizes="100vw" type="image/webp" />
          <link rel="preload" as="image" href="/images/eco-cabin-hero.jpeg" type="image/jpeg" />
        </>
      )}
      {/* Preload LCP image for blog page dynamically */}
      {isBlogPage && lcpImage && renderLcpPreloads(lcpImage)}

      {/* Preload posts.json for blog page to eliminate network dependency chain */}
      {isBlogPage && (
        <>
          <link rel="preload" href="/api/posts.json" as="fetch" crossorigin="anonymous" />
          {/* Dynamic modulepreload for critical JavaScript modules */}
          {getBlogPageCriticalModulesStatic().map((modulePath, index) => (
            <link key={index} rel="modulepreload" href={modulePath} />
          ))}
        </>
      )}

      {/* OpenGraph tags */}
      <meta property="og:type" content={seo.openGraph.type} />
      <meta property="og:title" content={seo.openGraph.title} />
      <meta property="og:description" content={seo.openGraph.description} />
      <meta property="og:url" content={seo.openGraph.url} />
      <meta property="og:site_name" content={seo.openGraph.siteName} />
      {seo.openGraph.images?.map((image, index) => (
        <meta
          key={`og:image:${index}`}
          property="og:image"
          content={image.url}
        />
      ))}

      {/* Twitter Card tags */}
      <meta name="twitter:card" content={seo.twitter.cardType} />
      <meta name="twitter:site" content={seo.twitter.site} />
      <meta name="twitter:creator" content={seo.twitter.handle} />
      <meta name="twitter:title" content={seo.openGraph.title} />
      <meta name="twitter:description" content={seo.openGraph.description} />
      {seo.openGraph.images?.[0] && (
        <meta name="twitter:image" content={seo.openGraph.images[0].url} />
      )}

      {children}
    </Helmet>
  );
} 